import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import StateObserver from 'src/StateObserver';
import { ImageScaleView, ImageSliceView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { animDuration, createWaiter, delay, parseAmount } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { trackPinchEvent } from 'src/lib/analytics/events';
import { Feature } from 'src/replicant/analytics';
import * as Inventory from 'src/replicant/inventory';
import { startGemsPinchSequence } from 'src/game/shop/Helpers';
import Match2PinchPanel from './Match2PinchPanel';
import { formatDuration } from 'src/lib/formatDuration';
import { PetEntity } from 'src/game/logic/EntityManager';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { duration } from 'src/replicant/utils/duration';
import Match2BuruStreakPanel from './Match2BuruStreakPanel';
import { isMatch2UnlockableUnlocked } from 'src/replicant/match2/match2.getters';
import Match2PinchLossPanel from './Match2PinchLossPanel';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { gsap } from 'gsap';
import { getGameScene } from '../game/getGameScene';
import { user } from 'src/StateObserver';
import { isMatch2FrenzyEnabled } from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import { matchTheme } from 'src/game/theme/match2/Match2Theme';

import { AssetGroup } from '@play-co/timestep-core';

export type Match2PinchType = 'energy' | 'moves';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupMatch2Pinch extends PopupBasic {
  private close: CloseHandler;
  private contentWrapper: View;
  private cancelButton: ButtonScaleViewWithText;
  private cost: LangBitmapFontTextView;
  private balance: LangBitmapFontTextView;
  private okButton: ButtonScaleViewWithText;
  private regenTimer!: LangBitmapFontTextView;
  private regenIcon!: ImageScaleView;
  private type!: Match2PinchType;
  private pet: PetEntity;
  private species: PetSpeciesID | undefined;
  private streak?: Match2BuruStreakPanel;
  private toggleMessage?: LangBitmapFontTextShadowView;
  private isPopupHidden = false;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const offset = 10;
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      height: ui.popup.height - offset,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_POPUP,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();
    this.message.removeFromSuperview();
    this.title.updateOpts({ size: 30 });
    this.bg.onClick = () => this.toggleShowPuzzle();

    this.contentWrapper = new View({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
    });

    const costRow = this.contentWrapper.addSubview(
      new ImageSliceView({ ...ui.costRow, y: ui.costRow.y - offset }),
    );

    costRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('purchaseWithGems.requiredGems'),
      }),
    );

    this.cost = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: costRow,
    });

    costRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        image: 'assets/ui/hud/icons/hud_gem.png',
        x: costRow.style.width - ui.currency.width,
      }),
    );

    const balanceRow = this.contentWrapper.addSubview(
      new ImageSliceView({ ...ui.balanceRow, y: ui.balanceRow.y - offset }),
    );

    balanceRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('purchaseWithGems.balanceAfterPurchase'),
      }),
    );

    this.balance = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: balanceRow,
    });

    balanceRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        image: 'assets/ui/hud/icons/hud_gem.png',
        x: balanceRow.style.width - ui.currency.width,
      }),
    );

    this.cancelButton = new ButtonScaleViewWithText({
      ...ui.backButton,
      superview: this.contentWrapper,
      y: ui.backButton.y - offset,
      localeText: () => i18n('basic.close'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close(false);
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_BACK,
      },
    });

    this.species = Pet.getters.getCurrentEvolution(user())?.species;
    this.pet = new PetEntity('minigame', this.species);
    this.pet.updateOpts({
      superview: this.contentWrapper,
      x: 140,
      y: 300,
      scale: 0.75,
      anchorX: 0.5 * this.pet.style.width,
      anchorY: 0.78 * this.pet.style.height,
    });

    this.okButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      y: ui.okButton.y - offset,
      superview: this.contentWrapper,
      localeText: () => i18n('basic.buy'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_OK,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/hud/icons/hud_gem.png',
      'assets/ui/hud/icons/hud_energy.png',
    ]);
  }

  private createBuruStreakPanel() {
    this.streak = new Match2BuruStreakPanel({
      superview: this.box,
      x: this.box.style.width * 0.5,
      y: this.box.style.height + 190,
    });
  }

  public init({
    type,
    amount,
    continues = 0,
    price,
    title,
    petAnimation,
    showStreak,
    objectivesCount = 0,
  }: {
    type: Match2PinchType;
    amount: number;
    continues?: number;
    price: number;
    title?: string;
    petAnimation?: 'sad' | 'cry';
    showStreak?: boolean;
    objectivesCount?: number;
  }) {
    super.init({
      title: i18n(title ?? `match2.${type}Pinch.title`),
    });

    this.cancelButton.localeText = () =>
      i18n(type === 'moves' ? 'match2.movesPinch.giveUp' : 'basic.close');

    const petAnimations = petAnimationNames[this.species ?? 'ball'];
    this.pet.setAnimation(
      petAnimation === 'cry' ? petAnimations.cry : petAnimations.very_sad.idle,
    );

    if (type === 'energy') {
      this.box.style.height += 70;
      this.contentWrapper.style.y = 55;

      this.regenTimer = new LangBitmapFontTextView({
        superview: this.box,
        font: 'Body',
        color: '#CB2934',
        size: 25,
        x: this.box.style.width * 0.5,
        y: 90,
        localeText: () => i18n('match2.energyPinch.timer'),
        centerOnOrigin: true,
        centerAnchor: true,
        verticalAlign: 'center',
        align: 'center',
      });

      this.regenIcon = new ImageScaleView({
        superview: this.regenTimer,
        image: `assets/ui/hud/icons/hud_energy.png`,
        width: 39,
        height: 46,
        centerOnOrigin: true,
        centerAnchor: true,
        x: 100 - this.regenTimer.getTextWidth() * 0.5,
        y: 12,
        scale: 0.8,
      });

      new ImageScaleView({
        superview: this.box,
        image: matchTheme().getUIAsset(`panel_separator.png`),
        width: 284,
        height: 5,
        centerOnOrigin: true,
        centerAnchor: true,
        x: this.box.style.width * 0.5,
        y: 128,
      });

      this.updateEnergyTimer();
    } else if (type === 'moves') {
      this.toggleMessage = new LangBitmapFontTextShadowView({
        superview: this.root,
        font: 'Body',
        color: '#FFFFFF',
        width: this.root.style.width,
        height: 100,
        size: 30,
        x: this.root.style.width * 0.5,
        y:
          100 +
          (showStreak
            ? 0
            : this.box.style.y + this.box.style.height * 0.5 + 100),
        localeText: () => i18n('match2.movesPinch.toggleMessage1'),
        centerOnOrigin: true,
        centerAnchor: true,
        verticalAlign: 'center',
        align: 'center',
        scaleY: 0,
        blockEvents: true,
        backgroundColor: 'rgba(0,0,0,0.7)',
      });
    }

    this.type = type;

    const now = StateObserver.now();

    if (showStreak && isMatch2FrenzyEnabled(user(), now)) {
      new Match2PinchLossPanel({
        superview: this.contentWrapper,
        objectivesCount,
        x: 395,
        y: 205,
      });
    } else {
      new Match2PinchPanel({
        superview: this.contentWrapper,
        type,
        continues,
        amount,
        x: 395,
        y: 205,
      });
    }

    this.cost.localeText = () => parseAmount(price);

    const balance = this.getBalanceAfterPurchase(price);
    this.balance.localeText = () => parseAmount(balance);

    this.okButton.onClick = async () => {
      sounds.sfx.play('ok');
      const canPurchase = this.canPurchase(price);
      let success = true;

      if (!canPurchase) {
        const gemsNeeded = balance < 0 ? Math.abs(balance) : 0;
        this.trackPinchEvent();
        await startGemsPinchSequence({
          feature: 'match2',
          pinch: 'gems',
          gemsNeeded,
          crossplayTracking:
            type === 'moves'
              ? {
                  feature: 'match_continue',
                  subFeature: `match_continue_gems`,
                  continueCount: continues,
                }
              : {
                  feature: 'energy',
                  subFeature: `energy_pinch`,
                },
        });
        success = this.canPurchase(price);
      }

      if (success) this.close(true);
    };

    if (!this.canPurchase(price)) {
      this.balance.updateOpts({ color: 'red' });
    }

    if (showStreak && isMatch2UnlockableUnlocked(user(), 'buruStreak')) {
      this.createBuruStreakPanel();
    }
  }

  private async toggleShowPuzzle() {
    if (!this.toggleMessage) return;
    this.isPopupHidden = !this.isPopupHidden;

    // Toggle CTAs interactivity accordingly
    this.box.updateOpts({ blockEvents: this.isPopupHidden });

    const duration = 0.5;
    gsap.to(this.bg.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });
    gsap.to(this.box.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });

    if (this.streak) {
      gsap.to(this.streak.style, {
        duration,
        opacity: this.isPopupHidden ? 0 : 1,
        overwrite: true,
      });
    }

    await this.tweenToggleMessageScaleY(0);
    this.toggleMessage.localeText = () =>
      i18n(`match2.movesPinch.toggleMessage${this.isPopupHidden ? 2 : 1}`);
    await this.tweenToggleMessageScaleY(1);
  }

  private async tweenToggleMessageScaleY(v: number) {
    if (!this.toggleMessage) return;
    await gsap.to(this.toggleMessage.style, {
      duration: 0.15,
      scaleY: v,
      ease: 'quad.inOut',
      overwrite: true,
    });
  }

  public override async fadeIn() {
    super.fadeIn();
    await delay(animDuration * 2);
    await this.streak?.show();
    this.streak?.flashProgress();
    await this.tweenToggleMessageScaleY(1);
  }

  public override async fadeOut() {
    void this.tweenToggleMessageScaleY(0);
    this.streak?.hide?.();
    super.fadeOut();
    await delay(animDuration * 2);
    this.streak?.unflashProgress();
  }

  private updateEnergyTimer() {
    const { user } = StateObserver.getState();
    const now = StateObserver.now();
    const time = Inventory.getters.getEnergyRegenerationConfig(user).time;
    const timeSinceLastEnergy =
      (now - user.inventory.energyRechargeStartTime) % time;
    const timeToNextEnergy = time - timeSinceLastEnergy;
    const locale = StateObserver.getLocale();

    this.regenTimer.updateOpts({
      localeText: () =>
        i18n('match2.energyPinch.timer', {
          time: formatDuration(timeToNextEnergy < 0 ? 0 : timeToNextEnergy),
        }),
    });

    const localeOffset = locale === 'en' ? 100 : 268;
    this.regenIcon.style.x =
      localeOffset - this.regenTimer.getTextWidth() * 0.5;

    createWaiter().wait(
      this.updateEnergyTimer.bind(this),
      duration({ seconds: 1 }),
    );
  }

  /**
   * Check if player can purchase care item
   */
  private canPurchase(price: number) {
    return this.getBalanceAfterPurchase(price) >= 0;
  }

  /**
   * Get balance after purchase, can be negative
   */
  private getBalanceAfterPurchase(price: number) {
    return Inventory.getters.getGems(user()) - price;
  }

  private trackPinchEvent() {
    trackPinchEvent(
      {
        resource: this.type,
        trigger: `match2_shop_${this.type}`,
      },
      getGameScene()?.getPinchAnalyticsData(),
    );
  }
}
